import { toast } from 'sonner'
import type { HaGateway, ServiceCall } from './types'
import { RealGateway } from './connection/real-gateway'
import { HASS_URL } from './connection/auth'
import { allEntityIds } from './registry'
import { debounceMs } from './services/policies'
import { predict } from './services/optimistic'
import { useEntitiesStore, rollbackIfStale } from '@/store/entities'
import { useConnectionStore } from '@/store/connection'

let gateway: HaGateway | null = null
const ROLLBACK_MS = 5000

/** Build the gateway for the active mode and wire it to the stores. */
export async function startGateway(): Promise<HaGateway> {
  if (gateway) return gateway

  const forceMock =
    import.meta.env.VITE_HA_MODE === 'mock' ||
    (typeof location !== 'undefined' && new URLSearchParams(location.search).has('mock'))
  if (forceMock) {
    const { MockGateway } = await import('@/mock/mock-gateway')
    gateway = new MockGateway()
  } else {
    gateway = new RealGateway()
  }

  gateway.onConnState((s) => useConnectionStore.getState().set(s))
  gateway.subscribeEntities(allEntityIds(), (diff) => useEntitiesStore.getState().ingest(diff))
  await gateway.start()
  return gateway
}

export function getGateway(): HaGateway {
  if (!gateway) throw new Error('gateway not started')
  return gateway
}

/**
 * Resolve an entity_picture / media-art path to a loadable URL. HA emits these as
 * origin-relative paths (e.g. `/api/media_player_proxy/...?token=…`); the real HA
 * lives on a different origin than this dashboard, so those must be prefixed with
 * HASS_URL. The mock serves its own assets from this origin, so they stay relative.
 * Absolute URLs (Spotify/Sonos art, etc.) pass through untouched.
 */
export function entityPictureUrl(picture: string | null | undefined): string | null {
  if (!picture) return null
  if (picture.startsWith('http')) return picture
  return gateway?.mode === 'real' ? `${HASS_URL}${picture}` : picture
}

// trailing-debounce timers keyed by `domain.service.entity`
const timers = new Map<string, ReturnType<typeof setTimeout>>()

function keyOf(call: ServiceCall): string {
  const t = call.target?.entity_id
  const id = Array.isArray(t) ? t.join(',') : (t ?? '')
  return `${call.domain}.${call.service}:${id}`
}

/**
 * Central service-call entry: applies optimistic state immediately, debounces
 * high-frequency calls (latest wins), sends, and schedules a rollback if the
 * prediction never confirms. Sensitive-action confirmation is enforced at the
 * UI layer (HoldToConfirm) before this is reached.
 */
export async function call(sc: ServiceCall): Promise<void> {
  const store = useEntitiesStore.getState()

  // optimistic prediction for the primary target entity
  const target = sc.target?.entity_id
  const primaryId = Array.isArray(target) ? target[0] : target
  if (primaryId) {
    const predicted = predict(sc, store.entities[primaryId])
    if (predicted) {
      store.applyOptimistic(predicted)
      const since = predicted.pendingSince!
      setTimeout(() => {
        if (rollbackIfStale(primaryId, since)) {
          toast.error('Fikk ikke svar fra Home Assistant', { description: 'Tilstanden ble tilbakestilt.' })
        }
      }, ROLLBACK_MS)
    }
  }

  const wait = debounceMs(sc)
  if (wait === 0) {
    await send(sc)
    return
  }

  const key = keyOf(sc)
  const existing = timers.get(key)
  if (existing) clearTimeout(existing)
  timers.set(
    key,
    setTimeout(() => {
      timers.delete(key)
      void send(sc)
    }, wait),
  )
}

async function send(sc: ServiceCall): Promise<void> {
  try {
    await getGateway().callService(sc)
  } catch (err) {
    toast.error('Handlingen feilet', { description: String(err) })
  }
}
